import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Logo } from "./Logo";
import { useTheme } from "./ThemeContext";
import { useAuth, type Operator } from "../auth/AuthContext";

// Backstage's shell is vendored from memex-ai's AppShell (dec-9): the same
// h-screen flex frame, the same w-60 left sidebar (Logo + nav menu items + a
// SidebarUserCard pinned to the bottom carrying the avatar, a user menu and the
// sun/moon theme toggle), beside a flex-1 main content area. The class names are
// copied verbatim so the look is identical; only the nav links, the user object
// and the user-menu items are Backstage's own.

interface NavLinkDef {
  to: string;
  label: string;
  icon: ReactNode;
}

const NAV_LINKS: ReadonlyArray<NavLinkDef> = [
  {
    to: "/",
    label: "Dashboard",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13h8V3H3v10zm10 8h8V11h-8v10zM3 21h8v-6H3v6zM13 3v6h8V3h-8z" />
      </svg>
    ),
  },
  {
    to: "/users",
    label: "Users",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
      </svg>
    ),
  },
  {
    to: "/ask",
    label: "Ask God",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
      </svg>
    ),
  },
  {
    to: "/live",
    label: "Live",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    to: "/experiments",
    label: "Experiments",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.233.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.719-.293-2.3-2.379-1.067-3.61L5 14.5" />
      </svg>
    ),
  },
  {
    to: "/comms",
    label: "Comms",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.76c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 011.037-.443 48.282 48.282 0 005.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
      </svg>
    ),
  },
];

// Bottom-of-sidebar identity card. Click avatar/name to open the menu (sign out).
// Theme toggle is a sibling button — visually next to the avatar but a separate
// action. Verbatim structure + classes from memex-ai's SidebarUserCard.
function SidebarUserCard({
  user,
  onLogout,
  isDark,
  onToggleTheme,
  collapsed,
}: {
  user: Operator;
  onLogout: () => void;
  isDark: boolean;
  onToggleTheme: () => void;
  collapsed: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const name = user.name ?? user.email;

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className={`relative flex items-center gap-2 ${collapsed ? "flex-col" : ""}`} ref={wrapperRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="user-card-button"
        title={collapsed ? `${name} · ${user.email}` : undefined}
        className={`min-w-0 flex items-center gap-2 rounded-lg transition-colors hover:bg-card-hover text-left ${collapsed ? "justify-center p-1" : "flex-1 px-2 py-1.5"}`}
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt={name}
            className="w-8 h-8 rounded-full flex-none"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-8 h-8 rounded-full flex-none flex items-center justify-center text-sm font-medium bg-btn-secondary text-secondary">
            {name.charAt(0).toUpperCase()}
          </div>
        )}
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate text-primary">{name}</p>
            <p className="text-xs text-muted truncate">{user.email}</p>
          </div>
        )}
      </button>
      <button
        onClick={onToggleTheme}
        data-testid="theme-toggle"
        className="flex-none p-1.5 rounded-lg transition-colors text-secondary hover:text-primary hover:bg-card-hover"
        title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      >
        {isDark ? (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
          </svg>
        )}
      </button>
      {open && (
        <div className="absolute left-0 right-0 bottom-full mb-2 z-40 rounded-lg shadow-xl py-1 border bg-card-hover border-edge">
          <button
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            data-testid="sign-out"
            className="w-full text-left px-3 py-2 text-sm transition-colors text-secondary hover:text-primary hover:bg-overlay"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function NavItem({ to, label, icon, collapsed }: NavLinkDef & { collapsed: boolean }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        [
          "flex items-center rounded-lg text-sm transition-colors",
          collapsed ? "justify-center px-2 py-2" : "gap-2.5 px-3 py-2",
          isActive
            ? "text-heading bg-card-hover font-medium"
            : "text-secondary hover:text-primary hover:bg-card-hover/60",
        ].join(" ")
      }
    >
      {icon}
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("backstage-nav-collapsed") === "true",
  );
  const toggleCollapsed = () =>
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem("backstage-nav-collapsed", String(next));
      return next;
    });

  return (
    <div className="h-screen flex overflow-hidden bg-page">
      <aside
        className={`${collapsed ? "w-16" : "w-60"} flex-none flex flex-col border-r border-edge bg-page transition-[width] duration-150`}
        aria-label="Primary navigation"
        data-testid="primary-nav"
      >
        <div className={`flex items-center py-4 ${collapsed ? "justify-center px-2" : "justify-between px-4"}`}>
          {!collapsed && (
            <Link to="/" className="flex items-center text-heading hover:text-heading">
              <Logo className="h-5" />
            </Link>
          )}
          <button
            onClick={toggleCollapsed}
            data-testid="nav-collapse"
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            title={collapsed ? "Expand" : "Collapse"}
            className="flex-none p-1.5 rounded-lg text-secondary transition-colors hover:bg-card-hover hover:text-primary"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
              <path strokeLinecap="round" strokeLinejoin="round" d={collapsed ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
            </svg>
          </button>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto px-3 space-y-0.5">
          {!collapsed && (
            <div className="pt-1 pb-1 px-3 text-xs font-medium uppercase tracking-wider text-muted">
              Control plane
            </div>
          )}
          {NAV_LINKS.map((link) => (
            <NavItem key={link.to} {...link} collapsed={collapsed} />
          ))}
        </nav>

        {user && (
          <div className="border-t border-edge p-3 space-y-2">
            <SidebarUserCard
              user={user}
              onLogout={logout}
              isDark={isDark}
              onToggleTheme={toggleTheme}
              collapsed={collapsed}
            />
          </div>
        )}
      </aside>

      <main className="flex-1 min-h-0 overflow-y-auto">{children}</main>
    </div>
  );
}
